import { ITopic, IOffering, IConsentForm, IConsentUpload,
    ITopicRating, IChartData, IEngagementRating,
    IHistoricalData } from "../interfaces/models";
import UserRepository from "../repositories/UserRepository";
import TopicRepository from "../repositories/TopicRepository";
import ImageService from "./ImageService";
import TopicService from "./TopicService";

interface IValidate {
    message: string,
    args: any,
    validateFunction: (args: any) => boolean
};

export default class UserService {
    static sendConsent(response: boolean) {
        return UserRepository.sendConsent(response);
    }

    static validateConsentForm(consentForm: IConsentForm) {
        const validators: IValidate[] = [{
            message: "Consent text cannot be empty",
            validateFunction: ImageService.domIsNotEmpty,
            args: consentForm.content
        }];
        for (let i = 0; i < validators.length; ++i) {
            const entry = validators[i];
            if (entry.validateFunction(entry.args) === false) {
                return entry.message;
            }
        }
        return "";
    }

    static prepareConsentUpload(consentForm: IConsentForm): Promise<IConsentUpload> {
        const upload: IConsentUpload = {
            payload: undefined,
            author: consentForm.author
        };

        return ImageService.extractImagesFromDOM(consentForm.content)
            .then(consentContent => {
                upload.payload = consentContent;
            })
            .then(() => upload);
    }

    static uploadContent(upload: IConsentUpload) {
        return UserRepository.uploadConsentForm(upload);
    }


    static generateTopicGraph(userData: ITopicRating[], peerData: ITopicRating[], exclude: number[]): IChartData {
        const topics = peerData.map(x => TopicRepository.topicPointer(x.topic))
            .filter(x => exclude.findIndex(e => e === x.id) === -1);
        const filteredPeerData = peerData.filter(x => exclude.findIndex(e => e === x.topic.id) === -1)
            .map(x => x.rating);
        const filteredUserData = topics.map(function(x) {
            let topicIndex = userData.map(y => y.topic).findIndex(e => e.id === x.id);
            if (topicIndex >= 0) {
                return userData[topicIndex].rating;
            } else {
                return 0;
            }
        });

        const userDataset = {
            label: "Your Results",
            data: filteredUserData
        };
        const peerDataset = {
            label: "Class Average",
            data: filteredPeerData,
            type: "line"
        };
        return {
            labels: topics.map(x => x.name),
            datasets: [userDataset, peerDataset],
            options: {}
        };
    }

    static generateEngagementGraph(userData: IEngagementRating[], peerData: IEngagementRating[],
            exclude: number[]): IChartData {
        const topics = userData.map(x => TopicRepository.engagementPointer(x.engagement))
            .filter(x => exclude.findIndex(e => e === x.id) === -1);
        const maxValues = userData.filter(x => exclude.findIndex(e => e === x.engagement.id) === -1)
            .map(x => x.maxNum);
        const filteredUserData = userData.filter(x => exclude.findIndex(e => e === x.engagement.id) === -1)
            .map((x, i) => Math.round(x.rating / maxValues[i] * 100));
        const filteredPeerData = peerData.filter(x => exclude.findIndex(e => e === x.engagement.id) === -1)
            .map((x, i) => Math.round(x.rating / maxValues[i] * 100));
        const label = function(t: any, d: any) {
            let label = d.labels[t.index];
            let value = Math.round(d.datasets[t.datasetIndex].data[t.index] / 100 * maxValues[t.index]);
            return label + ": " + value;
        };

        const userDataset = {
            label: "Your Results",
            data: filteredUserData,
            type: "radar"
        };
        const peerDataset = {
            label: "Peer Results",
            data: filteredPeerData,
            type: "line"
        };
        return {
            labels: topics.map(x => x.name),
            datasets: [userDataset, peerDataset],
            options: {
                tooltips: {
                    callbacks: {
                        label: label
                    }
                }
            }
        };
    }

    static generateHistoricalGraph(data: IHistoricalData, exclude: number[]): IChartData {
        let topics = data.topicData.map(x => TopicService.topicByName(x.topic));
        topics = topics.filter(x => exclude.indexOf(x!.id) == -1);
        let datasets = [];
        for (let topic of topics) {
            let topicIndex = data.topicData.map(x => x.topic).indexOf(topic!.name);
            datasets.push({
                label: topic!.name,
                data: data.topicData[topicIndex].rating,
                type: "line"
            });
        }
        return {
            datasets: datasets,
            labels: data.labels,
            options: {}
        };
    }

    static userCompetencies({ dataType, exclude, userID }:
            { dataType?: string, exclude?: undefined | number[], userID: number }) {
        const excludeTopics = exclude || [];
        if (dataType == "historical") {
            return UserRepository.getHistoricalCompetencies(userID).then(
                data => UserService.generateHistoricalGraph(data, excludeTopics)
            );
        } else {
            return Promise.all([UserRepository.getUserCompetencies(userID), UserRepository.getCompareAgainst()])
               .then(data => UserService.generateTopicGraph(data[0], data[1], excludeTopics));
        }
    }

    static getHistoricalRatings(userID: number) {
        return UserRepository.getHistoricalCompetencies(userID);
    }

    static getEngagementScores({ _dataType, exclude, userID }:
            { _dataType: string, exclude?: undefined | number[], userID: number }) {
        const excludeTopics = exclude || [];
        const compareTo = "peers";
        return Promise.all([UserRepository.getUserEngagement(userID), UserRepository.getEngagementAgainst(compareTo)])
            .then(data => UserService.generateEngagementGraph(data[0], data[1], excludeTopics));
    }

    static getAllOfferingUsers(offeringID: string) {
        return UserRepository.getAllOfferingUsers(offeringID);
    }

    static getAllAvailableEngagementTypes() {
        return UserRepository.getAllAvailableEngagementTypes();
    }

    static getUserPeers({ count }: { count: number }) {
        return UserRepository.getUserConnections(count);
    }

    static getEngagementSummary() {
        return UserRepository.getUserEngagement(undefined)
            .then(y => y.map(x => ({
                node: x.engagement,
                score: x.rating
            })));
    }

    static getAuthoredQuestions(userID: number, pageSize: number, pageIndex: number) {
        return UserRepository.getAuthoredQuestions(userID, pageSize, pageIndex);
    }

    static getLoggedInUser() {
        return UserRepository.getLoggedInUser();
    }

    static getConsentForm() {
        return UserRepository.getConsentForm();
    }
    static getUserConsentFormResponse() {
        return UserRepository.getUserConsentFormResponse();
    }

    static getAllAvailableCategories() {
        return UserRepository.getAllAvailableCategories();
    }

    static getRecommendedConnections({ count }: { count: number }) {
        return UserRepository.getUserConnections(count);
    }

    static getOutstandingRequests({ count }: { count: number }) {
        return UserRepository.getUserConnections(count);
    }

    static updateUserImage({ newImage }: { newImage: string }) {
        return UserRepository.updateUserImage(newImage);
    }

    static getMostReputableUsers({ sortField, sortOrder, leaderboardStartDate, leaderboardEndDate }:
        { sortField: string, sortOrder: "DESC" | "ASC", leaderboardStartDate?: Date,
            leaderboardEndDate?: Date}) {
        return UserRepository.getUserLeaderboard(sortField, sortOrder, leaderboardStartDate,
            leaderboardEndDate);
    }

    static getUserStats(startDate?: Date, endDate?: Date) {
        return UserRepository.getUserStats(startDate, endDate);
    }

    static getConsentedUserStats(startDate?: Date, endDate?: Date) {
        return UserRepository.getConsentedUserStats(startDate, endDate);
    }

    static getUserTopicReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getUserTopicReport(startDate, endDate);
    }

    static getUserTopicHistoryReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getUserTopicHistoryReport(startDate, endDate);
    }

    static getQuestionReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getQuestionReport(startDate, endDate);
    }

    static getAnswerReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getAnswerReport(startDate, endDate);
    }

    static getCommentReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getCommentReport(startDate, endDate);
    }

    static getConsentReport(startDate?: Date, endDate?: Date) {
        return UserRepository.getConsentReport(startDate, endDate);
    }

    static getCourseStats() {
        return UserRepository.getOfferingStats();
    }

    static getUserNotifications() {
        return UserRepository.getUserNotifications();
    }

    static markNotificationAsRead(id: number) {
        return UserRepository.markNotificationAsRead(id);
    }

    static getMeetingHistory() {
        return UserRepository.getMeetingHistory();
    }

    static getUserOfferings() {
        return UserRepository.getUserOfferings();
    }

    static updateOffering(data: { offering: IOffering, topics: ITopic[] }) {
        return UserRepository.updateOffering(data.offering, data.topics);
    }

    static getUniversities(): Promise<string[]> {
        return UserRepository.getUniversities();
    }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader!./src/services/UserService.ts